import {
	createColorScheme,
	createTheme,
	CssBaseline,
	type ThemeOptions,
	ThemeProvider,
	useColorScheme
} from "@mui/material";
import {AppRoutes} from "@components/default/AppRoutes.tsx";

import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {LocalizationProvider} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import 'dayjs/locale/pt-br'
import './app.css'
import updateLocale from 'dayjs/plugin/updateLocale'
import {ptBR as ptBrLocalePicker} from "@mui/x-date-pickers/locales";
import {Analytics} from "@vercel/analytics/react"
import {SpeedInsights} from "@vercel/speed-insights/react"
import {ptBR} from '@mui/x-data-grid/locales';
import '@mantine/core/styles.css'
import '@mantine/dates/styles.css'
import '@kevalgangani/mantine-table/styles.css'
import type {ReactNode} from "react";
import {MantineProvider} from '@mantine/core';
import {DatesProvider} from '@mantine/dates';
import {type AppThemeScheme, mantineThemes} from "./assets/theme/mantineThemes.ts";

dayjs.locale('pt-br');
dayjs.extend(updateLocale);

dayjs.updateLocale('pt-br', {
	weekdaysMin: ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'],
})


const baseTheme = {
	shape: {
		borderRadius: 2
	},
	components: {
		MuiTextField: {
			defaultProps: {
				fullWidth: true,
				variant: 'filled'
			}
		},
		MuiDatePicker: {
			defaultProps: {
				slotProps: {
					actionBar: {
						actions: ['today', 'clear']
					}
				}
			}
		},
		MuiTabs: {
			defaultProps: {
				variant: 'scrollable'
			}
		},
		MuiTooltip: {
			defaultProps: {
				enterDelay: 450,
				leaveDelay: 250,
				enterNextDelay: 100,
				arrow: true,
				placement: 'auto',
			}
		},
		MuiButton: {
			styleOverrides: {
				root: {
					"&.Mui-disabled": {
						opacity: 0.55,
						filter: "saturate(0.65)",
						boxShadow: "none",
					},
					"&.MuiButton-contained.Mui-disabled": {
						backgroundColor: "var(--variant-containedBg)",
						color: "var(--variant-containedColor)",
					},
					"&.MuiButton-outlined.Mui-disabled": {
						backgroundColor: "transparent",
						color: "var(--variant-outlinedColor)",
						borderColor: "var(--variant-outlinedBorder)",
					},
					"&.MuiButton-text.Mui-disabled": {
						backgroundColor: "transparent",
						color: "var(--variant-textColor)",
					},
				},
			},
		},
		MuiCssBaseline: {
			styleOverrides: (theme: any) => ({
				body: {
					'&::before': {
						content: '""',
						position: 'fixed',
						top: '-50%',
						left: '-50%',
						width: '200%',
						height: '200%',
						zIndex: -9999,
						backgroundImage: theme.palette.background.logoBg || "url('/BalyGo-halloween.svg')",
						backgroundRepeat: 'repeat',
						backgroundSize: '80px',
						opacity: 0.65,
						transform: 'rotate(45deg)',
						pointerEvents: 'none',
					}
				},
				'@media print': {
					'body::before': {
						display: 'none !important'
					}
				}
			})
		}
	},
	colorSchemes: {}
} as ThemeOptions

const defaultTheme = {
	...baseTheme,
	defaultColorScheme: 'tropical' as any,
	colorSchemes: {
		dark: {
			palette: {
				mode: 'dark',
				primary: {
					main: '#FDD835', // Amarelo (Yellow 600)
					light: '#FFFF72',
					dark: '#C6A700',
					contrastText: '#000000'
				},
				secondary: {
					main: '#FFA000', // Amber
					light: '#FFD149',
					dark: '#C67100',
					contrastText: '#000000'
				},
				info: {main: '#64B5F6'},
				warning: {main: '#ED6C02'}, // Laranja mais profundo para diferenciar do amarelo/âmbar
				success: {main: '#81C784'},
				error: {main: '#E57373'},
				background: {
					default: '#121212',
					paper: '#1E1E1E',
					appBar: '#1E1E1E',
					logoBg: "url('/BalyGo-halloween.svg')"
				}
				,
				text: {
					primary: '#EDEDED',
					secondary: '#BDBDBD',
					disabled: '#9E9E9E'
				},
				divider: '#444444'
			}
		}
	}
} as ThemeOptions

const energyTheme = {
	palette: {
		primary: {
			main: '#FB8C00',
			light: '#FBA333',
			dark: '#AF6200',
			contrastText: 'rgba(0, 0, 0, 0.87)'
		},
		secondary: {
			main: '#4A148C',
			light: '#7C43BD',
			dark: '#1B003A',
			contrastText: '#FFFFFF'
		},
		info: {main: '#218fe9ff'},
		warning: {main: '#E65100'}, // Laranja bem escuro/queimado
		success: {main: '#43A047'},
		error: {main: '#E53935'},
		background: {
			default: '#FCFBF2',
			paper: '#FFFFFF',
			appBar: '#fdd835',
			logoBg: "url('/BalyGo-halloween.svg')"
		},
		text: {
			primary: 'rgba(0, 0, 0, 0.87)',
			secondary: 'rgba(0, 0, 0, 0.6)',
			disabled: 'rgba(0, 0, 0, 0.38)'
		},
		divider: '#9E9E9E'
	}
}


const intencionTheme = {
	palette: {
		primary: {
			main: '#2B3292',
			light: '#4791DB',
			dark: '#115293',
			contrastText: 'rgba(255, 255, 255, 0.87)'
		},
		secondary: {
			main: '#8C92AC',
			light: '#33C9DC',
			dark: '#008394',
			contrastText: 'rgba(0, 0, 0, 0.87)'
		},
		info: {main: '#42A5F5'},
		warning: {main: '#E65100'}, // Laranja bem escuro/queimado
		success: {main: '#43A047'},
		error: {main: '#E53935'},
		background: {
			default: '#F2F4F7',
			paper: '#FFFFFF',
			appBar: 'primary.main',
			logoBg: "url('/BalyGo-halloween.svg')"
		},
		text: {
			primary: 'rgba(0, 0, 0, 0.87)',
			secondary: 'rgba(0, 0,  0,0.6)',
			disabled: 'rgba(255, 255, 255, 0.38)'
		},
		divider: '#9E9E9E'
	}
}
const intencionScheme = createColorScheme({...intencionTheme})
const energyScheme = createColorScheme({...energyTheme})
const masterGoldScheme = createColorScheme({...defaultTheme.colorSchemes.dark as any})

function MantineThemeBridge({children}: { children: ReactNode }) {
	const {mode, colorScheme} = useColorScheme();
	const activeScheme = (colorScheme as AppThemeScheme) || 'tropical';
	const activeMode = mode === 'dark' || activeScheme === 'masterGold' ? 'dark' : 'light';
	const selectedTheme = mantineThemes[activeScheme] ?? mantineThemes.tropical;

	return (
		<MantineProvider
			theme={selectedTheme}
			forceColorScheme={activeMode}
		>
			<DatesProvider settings={{ locale: 'pt-br', firstDayOfWeek: 0, weekendDays: [0, 6] }}>
				{children}
			</DatesProvider>
		</MantineProvider>
	);
}

export function App() {
	return (
		<div onContextMenu={(e) => {
			if (window.getSelection()?.toString() == '') {
				e.preventDefault()
			}
		}}>
			<span className={'no-print'} style={{
				position: 'fixed',
				top: 2,
				right: 2,
				zIndex: 9999999,
				fontStyle: 'italic',
				fontSize: 'small',
				color: 'dimgray'
			}}>V: {__APP_VERSION__}</span>
			<ThemeProvider theme={createTheme({
				...defaultTheme,
				colorSchemes: {
					masterGold: masterGoldScheme,
					tropical: energyScheme,
					intencion: intencionScheme,
				} as any
			}, ptBR)}
			               defaultMode="light"
			               colorSchemeStorageKey={"balygo-theme"}
			               disableTransitionOnChange>
				<CssBaseline/>
				<MantineThemeBridge>
					<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={"pt-br"}
					                      localeText={ptBrLocalePicker.components.MuiLocalizationProvider.defaultProps.localeText}>
						<AppRoutes/>
					</LocalizationProvider>
				</MantineThemeBridge>
			</ThemeProvider>
			<Analytics/>
			<SpeedInsights/>
		</div>
	)
}
